import type { SmartCreationBrowserDraft, SmartCreationStructure } from "@adpilot/shared";

export interface StructureSelectionQueueOptions {
  getPlan(): SmartCreationBrowserDraft | null;
  canEdit(): boolean;
  canContinue(): boolean;
  project(structure: SmartCreationStructure | null, pending: boolean): void;
  save(
    plan: SmartCreationBrowserDraft,
    structure: SmartCreationStructure,
  ): Promise<SmartCreationBrowserDraft>;
  commit(plan: SmartCreationBrowserDraft, latest: SmartCreationStructure): void;
  failed(error: unknown, plan: SmartCreationBrowserDraft): Promise<void>;
}

/** Selection stays immediate; serial CAS writes coalesce clicks to the latest desired structure. */
export class StructureSelectionQueue {
  private desired: SmartCreationStructure | null = null;
  private running = false;
  private epoch = 0;
  private waiters: Array<(accepted: boolean) => void> = [];

  constructor(private readonly options: () => StructureSelectionQueueOptions) {}

  isPending(): boolean {
    return this.running;
  }

  reset(): void {
    this.epoch += 1;
    this.running = false;
    this.desired = null;
    this.waiters.splice(0).forEach((resolve) => {
      resolve(false);
    });
    this.options().project(null, false);
  }

  select(structure: SmartCreationStructure): void {
    const options = this.options();
    const plan = options.getPlan();
    if (!plan || !options.canEdit()) return;
    if (!this.running && plan.draft.structure === structure) return;
    this.desired = structure;
    options.project(structure, true);
    if (!this.running) void this.run(this.epoch);
  }

  flush(): Promise<boolean> {
    return this.running
      ? new Promise((resolve) => this.waiters.push(resolve))
      : Promise.resolve(true);
  }

  private async run(epoch: number): Promise<void> {
    this.running = true;
    let accepted = true;
    while (epoch === this.epoch && this.desired) {
      const options = this.options();
      const plan = options.getPlan();
      const requested = this.desired;
      if (!plan) {
        accepted = false;
        break;
      }
      if (plan.draft.structure === requested) break;
      try {
        const saved = await options.save(plan, requested);
        if (epoch !== this.epoch) return;
        this.options().commit(saved, this.desired!);
      } catch (error) {
        if (epoch !== this.epoch) return;
        accepted = false;
        this.desired = null;
        this.options().project(null, true);
        await this.options().failed(error, plan);
        if (epoch !== this.epoch) return;
        if (this.desired && this.options().canContinue()) continue;
        break;
      }
    }
    if (epoch !== this.epoch) return;
    this.running = false;
    this.desired = null;
    this.options().project(null, false);
    this.waiters.splice(0).forEach((resolve) => {
      resolve(accepted);
    });
  }
}
